import path from "node:path";

const MAX_SLUG_LENGTH = 40;

/**
 * Convert an arbitrary plan name into a short kebab-case slug suitable
 * for a filename. ASCII-only, lowercase, punctuation stripped, collapsed
 * hyphens, truncated at a word boundary to {@link MAX_SLUG_LENGTH} chars.
 * Returns an empty string if no usable characters remain.
 */
export function slugifyPlanName(name: string): string {
  if (typeof name !== "string") {
    return "";
  }
  // Normalize unicode to split accents, then drop non-ASCII.
  const ascii = name.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  // Replace anything that isn't a-z / 0-9 with a hyphen.
  let slug = ascii
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (slug.length === 0) {
    return "";
  }
  if (slug.length > MAX_SLUG_LENGTH) {
    const truncated = slug.slice(0, MAX_SLUG_LENGTH);
    const lastHyphen = truncated.lastIndexOf("-");
    // Prefer a word-boundary cut if the hyphen isn't too close to the start.
    slug =
      lastHyphen >= Math.floor(MAX_SLUG_LENGTH / 2) ? truncated.slice(0, lastHyphen) : truncated;
    slug = slug.replace(/^-+|-+$/g, "");
  }
  return slug;
}

/**
 * Extract the `name` value from a leading YAML frontmatter block. Returns
 * `null` if there is no frontmatter, or if no `name:` key exists within it.
 * Handles single-quoted, double-quoted, and unquoted scalar values.
 */
export function extractPlanNameFromFrontmatter(content: string): string | null {
  if (typeof content !== "string" || content.length === 0) {
    return null;
  }
  // Allow a BOM and/or leading whitespace before the opening delimiter.
  const stripped = content.replace(/^\uFEFF/, "");
  const match = stripped.match(/^---\r?\n([\s\S]*?)\r?\n---/);
  if (!match) {
    return null;
  }
  const block = match[1];
  const lines = block.split(/\r?\n/);
  for (const line of lines) {
    // Only top-level keys (no leading indentation).
    const m = line.match(/^name\s*:\s*(.*?)\s*$/);
    if (!m) {
      continue;
    }
    let raw = m[1];
    if (raw.length === 0) {
      return null;
    }
    // Strip matching surrounding quotes.
    if (
      (raw.startsWith('"') && raw.endsWith('"') && raw.length >= 2) ||
      (raw.startsWith("'") && raw.endsWith("'") && raw.length >= 2)
    ) {
      raw = raw.slice(1, -1);
    }
    const value = raw.trim();
    return value.length > 0 ? value : null;
  }
  return null;
}

export interface ResolvePlanFilenameOptions {
  originalPath: string;
  newSlug: string;
  existsSync: (filePath: string) => boolean;
}

/**
 * Build the target absolute path for a renamed plan file. Preserves the
 * original directory, uses `<slug>.md`, and adds `-2`, `-3`, … suffixes
 * to avoid collisions with unrelated files.
 */
export function resolvePlanFilename(options: ResolvePlanFilenameOptions): string {
  const { originalPath, newSlug, existsSync } = options;
  const dir = path.dirname(originalPath);
  const base = `${newSlug}.md`;
  let candidate = path.join(dir, base);
  let counter = 2;
  while (candidate !== originalPath && existsSync(candidate)) {
    candidate = path.join(dir, `${newSlug}-${counter}.md`);
    counter += 1;
  }
  return candidate;
}
